import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Star, BadgeCheck } from 'lucide-react';
import { useAuth, formatApiErrorDetail } from '../../context/AuthContext';
import { ACCENTS } from './theme';

const API = process.env.REACT_APP_BACKEND_URL;

export const Stars = ({ value, size = 'w-4 h-4' }) => (
  <span className="inline-flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} className={`${size} ${i <= Math.round(value) ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}`} />
    ))}
  </span>
);

const ReviewForm = ({ slug, onPosted }) => {
  const [rating, setRating] = useState(5);
  const [title, setTitle] = useState('');
  const [comment, setComment] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await axios.post(`${API}/api/products/${slug}/reviews`, { rating, title, comment }, { withCredentials: true });
      setTitle(''); setComment('');
      onPosted();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-gray-50 rounded-2xl border border-gray-100 p-6" data-testid="review-form">
      <h3 className="font-bold text-gray-900 mb-4">Write a Review</h3>
      <div className="flex items-center gap-2 mb-4">
        <span className="text-sm text-gray-600">Your rating:</span>
        {[1, 2, 3, 4, 5].map((i) => (
          <button key={i} type="button" onClick={() => setRating(i)} data-testid={`review-star-${i}`} aria-label={`${i} stars`}>
            <Star className={`w-6 h-6 transition-colors ${i <= rating ? 'text-amber-400 fill-amber-400' : 'text-gray-300 hover:text-amber-300'}`} />
          </button>
        ))}
      </div>
      <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Review title (optional)" data-testid="review-title-input"
        className="w-full px-4 py-2.5 border border-gray-200 rounded-lg mb-3 focus:outline-none focus:ring-2 focus:ring-emerald-500" />
      <textarea value={comment} onChange={(e) => setComment(e.target.value)} required minLength={10} rows={3} data-testid="review-comment-input"
        placeholder="Share your experience with this robot (min 10 characters)…"
        className="w-full px-4 py-2.5 border border-gray-200 rounded-lg mb-3 focus:outline-none focus:ring-2 focus:ring-emerald-500" />
      {error && <p className="text-sm text-red-600 mb-3" data-testid="review-error">{error}</p>}
      <button type="submit" disabled={busy} data-testid="review-submit-btn"
        className="bg-emerald-600 hover:bg-emerald-500 disabled:opacity-60 text-white px-6 py-2.5 rounded-lg font-semibold transition-all">
        {busy ? 'Posting…' : 'Submit Review'}
      </button>
      <p className="text-xs text-gray-400 mt-2">Only verified buyers can post reviews.</p>
    </form>
  );
};

export const ProductReviews = ({ product, onAggregate }) => {
  const { user } = useAuth();
  const t = ACCENTS[product.accent];
  const [data, setData] = useState(null);

  const load = () => {
    axios.get(`${API}/api/products/${product.slug}/reviews`)
      .then(({ data }) => {
        setData(data);
        if (data.count > 0 && onAggregate) onAggregate(data);
      })
      .catch(() => setData({ reviews: [], count: 0, average: null }));
  };

  useEffect(load, [product.slug]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <section className="py-20 px-6 lg:px-12 bg-gray-50" data-testid="product-reviews">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-10">
          <span className={`${t.label} font-semibold uppercase tracking-wide text-sm`}>Customer Reviews</span>
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mt-2 mb-3">What Buyers Say</h2>
          {data && data.count > 0 ? (
            <div className="flex items-center justify-center gap-3" data-testid="reviews-aggregate">
              <Stars value={data.average} size="w-5 h-5" />
              <span className="font-bold text-gray-900">{data.average}</span>
              <span className="text-gray-500 text-sm">({data.count} verified review{data.count > 1 ? 's' : ''})</span>
            </div>
          ) : (
            <p className="text-gray-500 text-sm" data-testid="no-reviews">No reviews yet — be the first verified buyer to review.</p>
          )}
        </div>

        {data && data.reviews.length > 0 && (
          <div className="space-y-4 mb-10" data-testid="reviews-list">
            {data.reviews.map((r) => (
              <div key={r.review_id} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-gray-900">{r.name}</span>
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
                      <BadgeCheck className="w-3.5 h-3.5" /> Verified Buyer
                    </span>
                  </div>
                  <span className="text-xs text-gray-400">{new Date(r.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                </div>
                <Stars value={r.rating} />
                {r.title && <p className="font-semibold text-gray-900 mt-2">{r.title}</p>}
                <p className="text-gray-600 mt-1">{r.comment}</p>
              </div>
            ))}
          </div>
        )}

        {user ? (
          <ReviewForm slug={product.slug} onPosted={load} />
        ) : (
          <div className="bg-white rounded-2xl border border-gray-100 p-6 text-center" data-testid="review-login-prompt">
            <p className="text-gray-600">
              Purchased this robot?{' '}
              <Link to="/login" className={`${t.link} font-semibold underline`}>Sign in</Link>{' '}
              to leave a verified review.
            </p>
          </div>
        )}
      </div>
    </section>
  );
};
